import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useTable } from "@refinedev/antd";
import { NextActivityCell, useNextActivity } from "@/components/history/NextActivity";
import {
  Input,
  Table,
  Button,
  theme,
  type TableColumnsType,
  Form,
  Grid,
  Tag,
  Flex,
  Typography,
  Select,
  Radio,
  message,
  Dropdown,
  Space,
  MenuProps,
  Tooltip,
  Popover,
  Checkbox,
} from "antd";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CatalogColumnHeaderCell } from "@/pages/catalog/CatalogColumnHeaderCell";
import { useColumnOrder } from "./utils/useColumnOrder";
import { useColumnVisibility } from "./utils/useColumnVisibility";
import { CrudFilters, HttpError, useNavigation } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { QuotesFilters } from "./quotesFilters";
import {
  PlusOutlined,
  CalculatorOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  ShopOutlined,
  DownloadOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { LateralModal } from "@/components/modals/LateralModal";
import { PaginationTotal } from "@/components/pagination-total";
import {
  axiosInstance,
  filterUrlGenerate,
  getPrioritizedStatus,
  getSortLabel,
  getStatusMap,
  useFormatLastUpdate,
} from "@/utilities";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { QuotesKanbanView } from "./kanban/list";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { utils } from "xlsx";
import {
  API_URL_QUOTES,
  API_URL_SELLERS,
  API_URL_STORES,
  LINK_MARKETPLACE,
  LINK_MARKETPLACE_HASH,
} from "@/services/XCONS_ENV";
import { useSelectedStores } from "@/config/context/SelectedItemsContext";
import {
  FilterTags,
  PresupuestoIcon,
  SellerIcon,
  resolveAppliedFilter,
  toCustomersFilterValue,
  toOperatorsFilterValue,
  useAppliedFilterValue,
} from "@/components";
import { useTranslation } from "react-i18next";
import { TFunction, i18n as I18nType } from "i18next";
import { Larrow } from "@/components/icon/Larrow";
import { StoreFilter } from "@/components/select-store-filter";
import { useStoreFilters } from "@/hooks/useStoreFilters";
import {
  filterOutOperators,
  useOperatorContacts,
} from "@/hooks/useOperatorContacts";
import { debounce } from "@/utilities/debounce";
const { Title, Text, Link } = Typography;
const { Search } = Input;
type View = "card" | "table";

import type { SorterResult, SortOrder } from "antd/es/table/interface";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";

/** El orden base de las columnas, tal como se declaran más abajo. Las claves son el
 * `dataIndex` de cada una, que es lo que el sorter ya usaba como `columnKey`. */
const QUOTES_COLUMN_KEYS = [
  "updatedAt",
  "id",
  "participations",
  "status",
  "region",
  "quoteSellers",
  "nextActivity",
];
/** Presupuesto no se oculta: es la columna que identifica la fila. Se mueve igual. */
const QUOTES_HIDEABLE_KEYS = QUOTES_COLUMN_KEYS.filter((k) => k !== "id");

// Fuera del componente a propósito: si la celda se define inline, AntD la desmonta en cada
// render y el arrastre de columnas se corta apenas empieza.
const QUOTES_TABLE_COMPONENTS = {
  header: {
    cell: (props: any) => (
      <CatalogColumnHeaderCell
        {...props}
        handleInline
        style={{
          ...props.style,
          textAlign: "left",
          height: 60,
          padding: "0 8px",
          verticalAlign: "middle",
        }}
      />
    ),
  },
  body: {
    cell: (props: any) => <td {...props} style={{ ...props.style, padding: 8 }} />,
  },
};

export const Quotes: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [storesSelectProps, setStoresSelectProps] = useState<any>();
  const [sellersSelectProps, setSellersSelectProps] = useState<any>();
  const [showAllMap, setShowAllMap] = useState<Record<string, boolean>>({});
  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
  const [view, setView] = useState<View>("table");
  const formatLastUpdate = useFormatLastUpdate();
  const screens = Grid.useBreakpoint();
  const [allQuotes, setAllQuotes] = useState<any[]>([]);
  const [allQuotesLoading, setAllQuotesLoading] = useState<boolean>(false);
  const [columnSortOrders, setColumnSortOrders] = useState<
    Record<string, SortOrder | null>
  >({});

  const { selectedStores } = useSelectedStores();
  const navigate = useNavigate();
  const operatorContactIds = useOperatorContacts();

  const { token } = theme.useToken();
  const { t, i18n }: { t: TFunction; i18n: I18nType } = useTranslation();
  const statusMap = getStatusMap(t);
  const operatorsAplicados = useAppliedFilterValue("operators");
  const customersAplicados = useAppliedFilterValue("customers");

  const onSearch = (params: any) => {
    const filters: CrudFilters = [];
    const {
      main_search,
      orderId,
      sellerCode,
      calendar,
      calendar_created,
      storeCode,
      status,
      price_product_from,
      price_product_to,
      region,
      city,
      operators,
      customers,
      participant_toggle,
      filters: existingFilters,
    } = params;

    if (existingFilters) {
      existingFilters.forEach((filter: any) => {
        filters.push(filter);
      });
    }

    // Build the filters array but skip entries with no value. Two filters
    // on the same field where one has value=undefined was tripping refine's
    // dedup logic and dropping the contactId filter under "Soy participante
    // = Todos" (participant_toggle undefined).
    const pushIfValue = (filter: any) => {
      const v = filter?.value;
      if (v === undefined || v === null || v === "") return;
      if (Array.isArray(v) && v.length === 0) return;
      filters.push(filter);
    };

    pushIfValue({
      field: "search",
      operator: "contains",
      value: main_search,
    });
    pushIfValue({ field: "orderId", operator: "eq", value: orderId });
    pushIfValue({
      field: "createdAt",
      operator: "between",
      value: calendar_created
        ? [
            dayjs(calendar_created[0])?.format(),
            dayjs(calendar_created[1]).format(),
          ]
        : undefined,
    });
    pushIfValue({
      field: "updatedAt",
      operator: "between",
      value: calendar
        ? [dayjs(calendar[0])?.format(), dayjs(calendar[1]).format()]
        : undefined,
    });
    pushIfValue({
      field: "quoteSellers.sellerCode",
      operator: "in",
      value: sellerCode,
    });
    // "Store" tiene dos valores vigentes según quién originó el presupuesto —
    // operador → `store`, cliente → `quoting` (ver alias en quoteDetail.tsx STEPS).
    // Filtrar sólo por `quoting` deja afuera a los que nacieron `store`.
    pushIfValue(
      status === "quoting"
        ? { field: "quoteSellers.state", operator: "in", value: ["quoting", "store"] }
        : { field: "quoteSellers.state", operator: "eq", value: status },
    );
    pushIfValue({
      field: "quoteSellers.total",
      operator: "between",
      value:
        price_product_from !== undefined && price_product_to !== undefined
          ? [price_product_from, price_product_to]
          : price_product_from !== undefined
            ? [price_product_from, Number.MAX_VALUE]
            : price_product_to !== undefined
              ? [0, price_product_to]
              : undefined,
    });
    pushIfValue({ field: "region", operator: "contains", value: region });
    pushIfValue({ field: "city", operator: "contains", value: city });
    // "Soy participante" filters by userId — backend resolves it to every
    // contact row belonging to the user and filters quotes where any of them
    // is a participant with a valid permission value. This avoids the JWT
    // contactId=0 problem and works across stores.
    pushIfValue({
      field: "participations.userId",
      operator: "eq",
      value: participant_toggle,
    });
    // Operadores (por afiliación) y Clientes, en lista (xcons-docs#308). Con el drawer cerrado se
    // sostiene el último aplicado; ver `resolveAppliedFilter`.
    const op = resolveAppliedFilter({
      field: "operators",
      formValue: operators,
      serialize: toOperatorsFilterValue,
      existingFilters,
      applied: operatorsAplicados,
    });
    if (!op.skip) pushIfValue({ field: "operators", operator: "eq", value: op.value });
    const cu = resolveAppliedFilter({
      field: "customers",
      formValue: customers,
      serialize: toCustomersFilterValue,
      existingFilters,
      applied: customersAplicados,
    });
    if (!cu.skip) pushIfValue({ field: "customers", operator: "eq", value: cu.value });

    // Don't call setFilters manually — refine applies the returned filters
    // via useTable's pipeline. Manual setFilters racing with the return was
    // suppressing refetches under some flows.
    return filters;
  };

  const {
    tableQuery: { data, isLoading, isFetching },
    searchFormProps,
    tableProps,
    filters,
    setFilters,
  } = useTable<HttpError>({
    resource: "quotes",
    pagination: {
      pageSize: FILAS_POR_PAGINA,
      mode: "server",
    },
    sorters: {
      mode: "server",
      initial: [
        {
          field: "updatedAt",
          order: "desc",
        },
      ],
    },
    syncWithLocation: true,
    onSearch: onSearch,
  });

  const onViewChange = (value: View) => {
    setView(value);
  };

  const isLoadingTable = !data || isFetching;

  const total = data?.total || 0;

  const dataSource = useMemo(() => data?.data || [], [isLoadingTable, data]);

  /**
   * Los stores donde este operador puede **operar**, que no son los mismos que administra.
   *
   * `includeSellerLinked` suma los stores a los que sus sellers están linkeados y activos. Sin eso,
   * un operador de seller —que no pertenece a ningún store— abre "Nuevo presupuesto" y no tiene
   * contra qué store crearlo.
   *
   * Es un opt-in y no el default del endpoint a propósito: la sección Stores de Administración pide
   * la misma URL sin el flag, porque vender a través de un store no es administrarlo. Alimenta el
   * desplegable de Nuevo presupuesto y el filtro por store de esta grilla — las dos son "dónde
   * puedo operar".
   */
  const getStores = async () => {
    try {
      const response = await axiosInstance.get(
        `${API_URL_STORES}?includeSellerLinked=true`,
      );
      const stores = response.data.data;
      setStoresSelectProps(stores);
    } catch (error) {
      console.error(error);
    }
  };

  const getSellers = async () => {
    try {
      const response = await axiosInstance.get(API_URL_SELLERS);
      const sellers = response.data.data;
      setSellersSelectProps(sellers);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getSellers();
    getStores();
  }, []);

  const KANBAN_PAGE_SIZE = 100;
  const KANBAN_CONCURRENCY = 6;
  const KANBAN_MAX_PAGES = 20;
  const KANBAN_TTL_MS = 2 * 60 * 1000;
  const KANBAN_DAYS = 30;

  const kanbanBaseParams = useMemo(
    () => ({
      limit: KANBAN_PAGE_SIZE,
      "filter.updatedAt": `$gte:${dayjs()
        .subtract(KANBAN_DAYS, "days")
        .toISOString()}`,
      "filter.participations.permissionCode": "$in:quote",
    }),
    [],
  );

  const hasActiveFilters = useMemo(() => {
    if (!filters || filters.length === 0) return false;

    return filters.some((f: any) => {
      if (!f) return false;
      const value = f.value;

      if (Array.isArray(value)) {
        return value.some(
          (v: any) => v !== undefined && v !== null && v !== "",
        );
      }

      return value !== undefined && value !== null && value !== "";
    });
  }, [filters]);

  const kanbanCacheRef = useRef<{
    key: string;
    at: number;
    data: any[];
  } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const buildKanbanKey = () => `${KANBAN_PAGE_SIZE}|${KANBAN_DAYS}d|quote`;

  const fetchKanbanPool = useCallback(async () => {
    // si hay filtros activos, no tiene sentido prefetch: retorna
    if (hasActiveFilters) return;

    const key = buildKanbanKey();
    // Cache warm?
    if (
      kanbanCacheRef.current &&
      kanbanCacheRef.current.key === key &&
      Date.now() - kanbanCacheRef.current.at < KANBAN_TTL_MS
    ) {
      setAllQuotes(kanbanCacheRef.current.data);
      return;
    }

    // cancelar fetch previo si existía
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setAllQuotesLoading(true);

    try {
      // 1) Primer page para conocer totalPages (y ya sumar esa data)
      const first = await axiosInstance.get(API_URL_QUOTES, {
        params: { page: 1, ...kanbanBaseParams },
        signal: controller.signal,
      });
      const firstData = first.data?.data ?? [];
      const totalPagesRaw = first.data?.meta?.totalPages ?? 1;
      const totalPages = Math.min(totalPagesRaw, KANBAN_MAX_PAGES);

      let pool = firstData;

      if (totalPages > 1) {
        // 2) Pages restantes en paralelo, con concurrencia
        const pages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

        for (let i = 0; i < pages.length; i += KANBAN_CONCURRENCY) {
          const slice = pages.slice(i, i + KANBAN_CONCURRENCY);
          const batch = await Promise.allSettled(
            slice.map((p) =>
              axiosInstance.get(API_URL_QUOTES, {
                params: { page: p, ...kanbanBaseParams },
                signal: controller.signal,
              }),
            ),
          );
          for (const r of batch) {
            if (r.status === "fulfilled") {
              pool = pool.concat(r.value.data?.data ?? []);
            }
          }
        }
      }

      setAllQuotes(pool);
      kanbanCacheRef.current = { key, at: Date.now(), data: pool };
    } catch (e: any) {
      if (e?.name !== "CanceledError" && e?.message !== "canceled") {
        console.error("Kanban fetch error", e);
      }
    } finally {
      setAllQuotesLoading(false);
    }
  }, [API_URL_QUOTES, hasActiveFilters, kanbanBaseParams]);

  useEffect(() => {
    if (view === "card" && !hasActiveFilters) {
      void fetchKanbanPool();
    } else if (view === "table") {
      // si salís del kanban, cancelá en curso
      abortRef.current?.abort();
    }
  }, [view, hasActiveFilters, fetchKanbanPool]);

  const kanbanDataSource = useMemo(() => {
    return hasActiveFilters ? dataSource : allQuotes;
  }, [hasActiveFilters, dataSource, allQuotes]);

  const downloadData = async () => {
    setLoadingDownload(true);
    try {
      const respuesta = await axiosInstance.get(
        `${API_URL_QUOTES}/?page=1&limit=${data?.total}&${filterUrlGenerate(
          filters,
        )}`,
      );

      const allData = respuesta.data.data;

      if (!allData || allData.length === 0) {
        message.error(t("message.noDataFound"));
        return;
      }

      const columnsToRemove = [
        "status",
        "shareHash",
        "regionId",
        "latitude",
        "longitude",
        "defaultBilling",
        "accessLevel",
      ];

      const filteredData = allData.map((item: any) => {
        const newItem = { ...item };
        columnsToRemove.forEach((column) => {
          delete newItem[column];
        });
        // Estado y total viven anidados en quoteSellers (igual que en la tabla):
        // estado = status priorizado del quote, total = suma de los sellers.
        const sellers = item.quoteSellers ?? [];
        const prioritizedStatus = getPrioritizedStatus(sellers);
        newItem.estado =
          statusMap[prioritizedStatus as keyof typeof statusMap]?.label ??
          prioritizedStatus;
        newItem.total = sellers.reduce(
          (sum: number, seller: any) => sum + Number(seller?.total ?? 0),
          0,
        );
        // El array anidado no serializa limpio a XLS; ya está representado por
        // estado + total.
        delete newItem.quoteSellers;
        return newItem;
      });

      const documentName = `quotes_xcons_${new Date().toISOString()}.xlsx`;
      const worksheet = utils.json_to_sheet(filteredData, {
        header: Object.keys(filteredData[0]),
      });
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, "Xcons data");
      XLSX.writeFile(workbook, documentName);

      message.success(t("message.archiveDownload"));
    } catch (error) {
      message.error(t("message.noDataFound"));
      console.error(error);
    } finally {
      setLoadingDownload(false);
    }
  };

  const toggleShowAll = (id: string) => {
    setShowAllMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));

    syncColumnHeights(id);
  };

  const syncColumnHeights = useCallback((rowIdToUpdate?: string) => {
    const rows = document.querySelectorAll(".ant-table-row");

    rows.forEach((row) => {
      const rowKey = row.getAttribute("data-row-key");
      if (rowIdToUpdate && rowKey !== rowIdToUpdate) return;

      const titleElement = row.querySelector(".title-container") as HTMLElement;
      const contactsElement = row.querySelector(
        ".contacts-container",
      ) as HTMLElement;

      const storeElement = row.querySelector(".store-container") as HTMLElement;
      const storesPermissionsElement = row.querySelector(
        ".storesPermissions-container",
      ) as HTMLElement;

      const sellerElement = row.querySelector(
        ".seller-container",
      ) as HTMLElement;
      const sellerPermissionsElement = row.querySelector(
        ".sellerPermissions-container",
      ) as HTMLElement;

      // Asegurar que todos los elementos existen
      if (
        !titleElement ||
        !contactsElement ||
        !storeElement ||
        !storesPermissionsElement ||
        !sellerElement ||
        !sellerPermissionsElement
      )
        return;

      // Resetear alturas para obtener las reales
      titleElement.style.minHeight = "24px";
      storeElement.style.minHeight = "24px";
      sellerElement.style.minHeight = "24px";

      // Esperar un frame para que se recalculen alturas reales
      requestAnimationFrame(() => {
        const contactsHeight = contactsElement.offsetHeight;
        const storesHeight = storesPermissionsElement.offsetHeight;
        const sellersHeight = sellerPermissionsElement.offsetHeight;

        titleElement.style.minHeight = `${contactsHeight}px`;
        storeElement.style.minHeight = `${storesHeight}px`;
        sellerElement.style.minHeight = `${sellersHeight}px`;
      });
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      syncColumnHeights();
    }, 0);

    return () => clearTimeout(timer);
  }, [showAllMap, dataSource]);

  const sumQuote = (r: any) =>
    (r?.quoteSellers ?? []).reduce(
      (acc: number, s: any) => acc + Number(s?.total ?? 0),
      0,
    );


  const getColumnSortOrder = (field: string): SortOrder | null =>
    columnSortOrders[field] ?? null;

  const classifyParticipants = (participants: any[] = []) => {
    const visible = filterOutOperators(participants, operatorContactIds);

    // Owner = position 0 (or lowest position as fallback). Sorting ASC puts
    // the owner first so the default slice(0,1) in the column always renders
    // the owner instead of whichever participation happened to sort first.
    const byPositionAsc = (a: any, b: any) =>
      (a.position ?? Number.MAX_SAFE_INTEGER) -
      (b.position ?? Number.MAX_SAFE_INTEGER);

    // El owner suele venir SIN participationType (participante directo del quote), así que
    // este grupo también hay que ordenarlo por position — antes no se ordenaba y el slice(0,1)
    // mostraba un participante arbitrario en vez del owner (position 0).
    const usersWithoutPermissions = visible
      .filter((p) => !p.participationType || p.participationType === "")
      .sort(byPositionAsc);

    const usersWithStorePermissions = visible
      .filter((p) => p.participationType === "store")
      .sort(byPositionAsc);

    const usersWithSellerPermissions = visible
      .filter((p) => p.participationType === "seller")
      .sort(byPositionAsc);

    return {
      usersWithoutPermissions,
      usersWithStorePermissions,
      usersWithSellerPermissions,
    };
  };

  // Next activity (008 F4): programada futura más próxima por presupuesto.
  const nextAct = useNextActivity(
    "quote",
    (dataSource as any[]).map((r) => r?.id).filter(Boolean),
  );

  const { order: columnOrder, reorder: reorderColumn } = useColumnOrder({
    storageKey: "xcons:quotes:listTable:columnOrder:v1",
    togglableKeys: QUOTES_COLUMN_KEYS,
  });
  const { visibleKeys, setVisibleKeys, isVisible } = useColumnVisibility({
    storageKey: "xcons:quotes:listTable:visibleColumns:v1",
    togglableKeys: QUOTES_HIDEABLE_KEYS,
  });
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );
  const onDragEndColumns = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      reorderColumn(String(active.id), String(over.id));
    }
  };
  const columnLabels: Record<string, string> = {
    updatedAt: t("products.import.table.date"),
    id: t("menu.label.quotes"),
    participations: t("menu.label.contacts"),
    status: t("orders.table.total"),
    region: t("quotes.location"),
    quoteSellers: t("posts.fields.status.title"),
    nextActivity: "Próxima actividad",
  };

  const columns: TableColumnsType<any> = [
    {
      key: "updatedAt",
      title: () => (
        <Tooltip
          title={getSortLabel(getColumnSortOrder("updatedAt"), {
            none: "Última actualización (Default)",
            asc: "Más recientes primero",
            desc: "Más antiguas primero",
          })}
        >
          <span style={{ display: "block" }}>{t("products.import.table.date")}</span>
        </Tooltip>
      ),
      dataIndex: "updatedAt",
      // Lo que mide "DD/MM/YY hh:mm a" más el padding de la celda.
      width: 150,
      sorter: true,
      showSorterTooltip: false,
      // Mismo formato que la columna de fechas de Pedidos.
      render: (updatedAt, record) => {
        return (
          <Flex gap={12} vertical>
            <Flex gap={4} vertical>
              <Text
                strong
                style={{ color: token.colorTextSecondary, fontSize: 12 }}
              >
                {t("orders.table.created")}
              </Text>
              <Text style={{ color: token.colorTextSecondary, whiteSpace: "nowrap" }}>
                {dayjs(record?.createdAt).format("DD/MM/YY hh:mm a")}
              </Text>
            </Flex>
            {record?.updatedAt && (
              <Flex gap={4} vertical>
                <Text
                  strong
                  style={{ color: token.colorTextSecondary, fontSize: 12 }}
                >
                  {t("quotes.modified")}
                </Text>
                <Text style={{ color: token.colorTextSecondary, whiteSpace: "nowrap" }}>
                  {dayjs(record.updatedAt).format("DD/MM/YY hh:mm a")}
                </Text>
              </Flex>
            )}
          </Flex>
        );
      },
    },
    {
      title: () => (
        <Tooltip
          title={getSortLabel(getColumnSortOrder("id"), {
            none: "Última actualización (Default)",
            asc: "Ascendente",
            desc: "Descendente",
          })}
        >
          <span style={{ display: "block" }}>{t("menu.label.quotes")}</span>
        </Tooltip>
      ),
      key: "id",
      dataIndex: "id",
      width: 300,
      className: "column-vertical-top",
      sorter: true,
      showSorterTooltip: false,
      render: (orderId, record) => {
        const showAll = showAllMap[orderId] || false;

        const { usersWithoutPermissions, usersWithStorePermissions, usersWithSellerPermissions } =
          classifyParticipants(record?.participations ?? []);

        const canExpand =
          (record?.quoteSellers?.length ?? 0) > 1 ||
          usersWithoutPermissions.length > 1 ||
          usersWithStorePermissions.length > 1 ||
          usersWithSellerPermissions.length > 1;

        return (
          <Flex vertical>
            <Flex
              align="start"
              justify="space-between"
              className="title-container"
              style={{ marginBottom: 25 }}
              gap={8}
            >
              <Typography.Paragraph
                style={{
                  margin: 0,
                  maxWidth: 330,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                ellipsis={{
                  tooltip: `#${orderId} ${record.name}`,
                }}
              >
                <Text strong style={{ color: token.colorLinkHover }}>
                  <a
                    href={`/quotes/${orderId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/quotes/${orderId}`);
                    }}
                    style={{ color: "inherit" }}
                  >{`#${orderId} ${record.name}`}</a>
                </Text>
              </Typography.Paragraph>
              {canExpand && (
                <Button
                  shape="round"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleShowAll(orderId);
                  }}
                  icon={showAll ? "-" : "+"}
                  iconPosition="end"
                />
              )}
            </Flex>

            <Flex vertical align="start" gap={4}>
              <div className="store-container">
                <Flex gap={4} align="center">
                  <ShopOutlined /> {record.store?.name}
                </Flex>
              </div>
              <div className="seller-container" style={{ width: "80%" }}>
                <Flex style={{ width: "100%" }} justify="space-between">
                  <Flex vertical align="start" gap={8}>
                    {record?.quoteSellers
                      .slice(0, showAll ? record.quoteSellers.length : 1)
                      .map((seller: any, index: number) => (
                        <Flex key={index} gap={4} align="center">
                          <SellerIcon style={{ color: token.colorTextSecondary }} />
                          <Text style={{ color: token.colorTextSecondary }}>
                            {seller.sellerCode}
                          </Text>
                        </Flex>
                      ))}
                  </Flex>
                </Flex>
              </div>
            </Flex>
          </Flex>
        );
      },
    },
    {
      key: "participations",
      title: t("menu.label.contacts"),
      className: "column-vertical-top",
      width: 250,
      dataIndex: "participations",
      render: (participations, record) => {
        const showAll = showAllMap[record.id] || false;
        const whiteSpace = <span style={{ color: "transparent" }}>_</span>;

        const {
          usersWithoutPermissions,
          usersWithStorePermissions,
          usersWithSellerPermissions,
        } = classifyParticipants(participations || []);

        return (
          <Flex vertical>
            {/* Usuarios sin permisos */}
            <div className="contacts-container" style={{ marginBottom: 25 }}>
              <Flex justify="space-between" align="start" gap={10}>
                {usersWithoutPermissions.length > 0 ? (
                  <Flex vertical align="start" gap={8}>
                    {usersWithoutPermissions
                      .slice(0, showAll ? usersWithoutPermissions.length : 1)
                      .map((user: any, index: number) => (
                        <Text
                          strong
                          key={`noperm-${user.contactId}-${index}`}
                          style={{ color: token.colorLinkHover }}
                        >
                          <Link href={`/contacts/${user?.contactId}`}>
                            {user.firstname} {user.lastname}
                          </Link>
                        </Text>
                      ))}
                  </Flex>
                ) : (
                  <Text style={{ color: token.colorTextSecondary }}>
                    {whiteSpace}
                  </Text>
                )}
              </Flex>
            </div>

            {/* Usuarios con permisos en Stores */}
            <div className="storesPermissions-container">
              {usersWithStorePermissions.length > 0 ? (
                <Flex vertical align="start">
                  {usersWithStorePermissions
                    .slice(0, showAll ? usersWithStorePermissions.length : 1)
                    .map((user: any, index: number) => (
                      <Text
                        key={`storeperm-${user.contactId}-${index}`}
                        style={{ marginBottom: 4 }}
                      >
                        {user.firstname} {user.lastname} {user.position === 0 ? `(${t("owner")})` : ""}
                      </Text>
                    ))}
                </Flex>
              ) : (
                <Text style={{ color: token.colorTextSecondary }}>
                  {whiteSpace}
                </Text>
              )}
            </div>

            {/* Usuarios con permisos en Sellers */}
            <div className="sellerPermissions-container">
              {usersWithSellerPermissions.length > 0 ? (
                <Flex vertical align="start">
                  {usersWithSellerPermissions
                    .slice(0, showAll ? usersWithSellerPermissions.length : 1)
                    .map((user: any, index: number) => (
                      <Text
                        key={`sellerperm-${user.contactId}-${index}`}
                        style={{
                          marginBottom: 4,
                          color: token.colorTextSecondary,
                        }}
                      >
                        {user.firstname} {user.lastname}
                      </Text>
                    ))}
                </Flex>
              ) : (
                <Text style={{ color: token.colorTextSecondary }}>
                  {whiteSpace}
                </Text>
              )}
            </div>
          </Flex>
        );
      },
    },
    {
      title: () => (
        <Tooltip
          title={getSortLabel(getColumnSortOrder("id"), {
            none: "Última actualización (Default)",
            asc: "Ascendente",
            desc: "Descendente",
          })}
        >
          <span style={{ display: "block" }}>{t("orders.table.total")}</span>
        </Tooltip>
      ),
      key: "status",
      dataIndex: "status",
      sorter: true,
      showSorterTooltip: false,
      render: (name, record) => {
        const totalSum = record.quoteSellers
          ? record.quoteSellers.reduce(
            (sum: any, seller: any) => sum + seller.total,
            0,
          )
          : 0;

        const formattedTotal = totalSum.toLocaleString("es-AR", {
          style: "currency",
          currency: "ARS",
        });

        return (
          <Flex vertical>
            <Title level={5} style={{ color: token.colorText }}>
              {formattedTotal}
            </Title>
          </Flex>
        );
      },
    },
    {
      title: () => (
        <Tooltip
          title={getSortLabel(getColumnSortOrder("region"), {
            none: "Ubicación (Default)",
            asc: "Ascendente",
            desc: "Descendente",
          })}
        >
          <span style={{ display: "block" }}>{t("quotes.location")}</span>
        </Tooltip>
      ),
      key: "region",
      dataIndex: "region",
      width: 250,
      sorter: true,
      showSorterTooltip: false,
      render: (region, record) => {
        const parts = [record?.street, record?.city, region].filter(
          (p) => typeof p === "string" && p.trim().length > 0,
        );
        return <Text>{parts.join(", ")}</Text>;
      },
    },
    {
      key: "quoteSellers",
      title: t("posts.fields.status.title"),
      dataIndex: "quoteSellers",
      sorter: true,
      render: (quoteSellers, record) => {
        const prioritizedStatus = getPrioritizedStatus(quoteSellers);

        const tagData =
          statusMap[prioritizedStatus as keyof typeof statusMap] ||
          statusMap.unknown;

        return (
          <Flex vertical align="start" justify="start" gap={8}>
            <Tag style={tagData.style}>{tagData.label}</Tag>

            <Text type="secondary">
              {t("quotes.viewed")} {record?.visits}
            </Text>
          </Flex>
        );
      },
    },
    {
      title: "Próxima actividad",
      key: "nextActivity",
      width: 140,
      render: (_: unknown, record: any) => (
        <NextActivityCell value={nextAct[String(record?.id)]} />
      ),
    },
  ];

  const visibleColumns = [
    ...columnOrder
      .map((key) => columns.find((c) => String(c.key) === key))
      .filter((c): c is (typeof columns)[number] => !!c && isVisible(String(c.key)))
      .map((c) => ({
        ...c,
        onHeaderCell: () => ({ columnKey: String(c.key) } as any),
      })),
    // El selector de columnas es una columna finita más, al final — igual que en el
    // Catálogo. No se arrastra ni se oculta: es el propio control.
    {
      key: "columnsMenu",
      width: 40,
      title: (
        <Popover
          trigger="click"
          placement="bottomRight"
          content={
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox.Group
                value={["id", ...visibleKeys]}
                onChange={(vals) =>
                  setVisibleKeys((vals as string[]).filter((k) => k !== "id"))
                }
              >
                <Space direction="vertical">
                  {QUOTES_COLUMN_KEYS.map((k) => (
                    <Checkbox key={k} value={k} disabled={k === "id"}>
                      {columnLabels[k]}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </div>
          }
        >
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Popover>
      ),
      render: () => null,
    },
  ] as TableColumnsType<any>;

  const items: MenuProps["items"] = storesSelectProps?.map((store: any) => ({
    key: String(store?.store_id ?? store?.storeId ?? store?.code),
    label: store?.name,
  }));

  const handleCreateQuote = async (storeId: number) => {
    try {
      const { data } = await axiosInstance.post(API_URL_QUOTES, { storeId });
      const newId = data?.quoteId;
      if (!newId) {
        message.error("No se recibió el id del presupuesto creado");
        return;
      }
      // Owner assignment (position=0, permissionCode=quote) is done server-side
      // in lifecycleService.create -> assignCreatorAsOwner.
      navigate(`/quotes/${newId}`);
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message || err?.response?.data?.error;
      message.error(
        Array.isArray(apiMsg) ? apiMsg.join(", ") : apiMsg || "Error al crear presupuesto",
      );
    }
  };

  const menuProps: MenuProps = {
    items,
    onClick: ({ key }) => {
      const store = storesSelectProps?.find(
        (s: any) => String(s.store_id ?? s.storeId ?? s.code) === key,
      );
      const sid = Number(store?.store_id ?? store?.storeId);
      if (Number.isFinite(sid)) {
        handleCreateQuote(sid);
      }
    },
  };

  interface HeaderRowProps {
    total?: number;
  }

  const HeaderRow = React.memo(({ total = 0 }: HeaderRowProps) => (
    <div
      style={{
        padding: view !== "table" ? "16px" : 0,
        backgroundColor: view !== "table" ? token.colorBgContainer : "none",
        borderRadius: view !== "table" ? "8px 8px 0 0" : "none",
      }}
    >
      <Flex flex="block" gap="large" wrap={screens.sm ? false : true}>
        <Form {...searchFormProps} style={{ width: "100%" }}>
          <Form.Item name="main_search">
            <Search
              disabled={isFetching}
              placeholder={t("quotes.search")}
              loading={isLoading}
              allowClear
              onChange={(e) => {
                // Antd's `<Input.Search>` does NOT fire `onSearch` when the
                // user clicks the clear (X) icon — only on Enter / Search
                // button. Detect the transition to empty here so the table
                // refetches and the previous `search` filter is dropped from
                // refine's filter list (pushIfValue skips undefined values).
                if (e.target.value === "") {
                  searchFormProps.form?.setFieldsValue({
                    main_search: undefined,
                  });
                  searchFormProps.form?.submit();
                }
              }}
              onSearch={(value) => {
                if (value.trim() === "") {
                  searchFormProps.form?.setFieldsValue({
                    main_search: undefined,
                  });
                }
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
        </Form>

        <Flex
          gap="large"
          justify="center"
          style={{ width: screens.sm ? "auto" : "100%" }}
        >
          <LateralModal
            disabled={isFetching}
            buttonName={t("buttons.filters")}
            title={t("filter.title.addFilter")}
          >
            <QuotesFilters
              searchFormProps={searchFormProps}
              filters={filters}
              storesSelectProps={storesSelectProps}
              // handleChangeStores={handleChangeStores}
              selectedStores={selectedStores}
              sellersSelectProps={sellersSelectProps}
              t={t}
            />
          </LateralModal>

          <Button
            disabled={isFetching}
            loading={loadingDownload}
            onClick={() => downloadData()}
            icon={<DownloadOutlined />}
            iconPosition="end"
          >
            {t("actions.export")}
          </Button>

          <Dropdown menu={menuProps} disabled={isFetching}>
            <Button type="primary">
              <Space>
                {t("quotes.new")}
                <PlusOutlined />
              </Space>
            </Button>
          </Dropdown>
        </Flex>
      </Flex>

      <FilterTags
        searchFormProps={searchFormProps}
        onSearch={onSearch}
        i18n={i18n}
        filters={filters}
        setFilters={setFilters}
        t={t}
      />

      <Flex justify={total > 0 ? "space-between" : "end"}>
        {total > 0 && (
          <Text style={{ color: token.colorTextSecondary }}>
            {total} {t("menu.label.quotes")}
          </Text>
        )}

        <Radio.Group
          value={view}
          onChange={(e) => onViewChange(e.target.value)}
        >
          <Radio.Button value="card">
            <AppstoreOutlined />
          </Radio.Button>
          <Radio.Button value="table">
            <UnorderedListOutlined />
          </Radio.Button>
        </Radio.Group>
      </Flex>
    </div>
  ));

  useStoreFilters({
    setFilters,
    field: "store.code",
    operator: "in",
  });

  return (
    <>
      <Flex gap={32} align="center" style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 0 }}>
          <PresupuestoIcon /> {t("menu.label.quotes")}
        </Title>
        <StoreFilter stores={storesSelectProps} />
      </Flex>

      {view === "table" ? (
        <DndContext
          sensors={dndSensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEndColumns}
        >
          <SortableContext
            items={visibleColumns
              .filter((c) => c.key !== "columnsMenu")
              .map((c) => String(c.key))}
            strategy={horizontalListSortingStrategy}
          >
            <Table
              {...tableProps}
              scroll={{ x: "max-content", y: 55 * 10.5 }}
              rowKey="id"
              title={() => <HeaderRow total={total} />}
              components={QUOTES_TABLE_COMPONENTS}
              dataSource={dataSource}
              columns={visibleColumns}
              loading={isLoadingTable}
              onRow={(record) => ({
                onClick: () => navigate(`/quotes/${record.id}`),
                style: { cursor: "pointer" },
              })}
              locale={{
                triggerAsc: t("table.triggerAsc"),
                triggerDesc: t("table.triggerDesc"),
                cancelSort: t("table.cancelSort"),
              }}
              pagination={{
                ...tableProps.pagination,
                showSizeChanger: true,
                pageSizeOptions: OPCIONES_POR_PAGINA,
                locale: {
                  items_per_page: t("table.itemsPerPage"),
                  prev_page: t("table.prevPage"),
                  next_page: t("table.nextPage"),
                  jump_to: t("table.jumpTo"),
                  jump_to_confirm: t("table.jumpToConfirm"),
                  page: t("table.page"),
                  next_5: t("table.next5"),
                  prev_5: t("table.prev5"),
                },
                showTotal: (total) => {
                  return <PaginationTotal total={total} entityName={t("quotes")} />;
                },
              }}
              onChange={(pagination, filters, sorter, extra) => {
                const SORT_FIELD_MAP: Record<string, string> = {
                  status: "quoteSellers.total",
                  quoteSellers: "quoteSellers.state",
                };

                const mapSorterField = (s: SorterResult<any>): SorterResult<any> => {
                  const field = String(s.columnKey || s.field || "");
                  const mapped = SORT_FIELD_MAP[field];
                  return mapped ? { ...s, field: mapped } : s;
                };

                const handleSorter = (s: SorterResult<any>) => {
                  const field = s.columnKey || s.field;
                  if (!field) return;
                  setColumnSortOrders((prev) => ({
                    ...prev,
                    [String(field)]: s.order ?? null,
                  }));
                };

                if (Array.isArray(sorter)) {
                  sorter.forEach(handleSorter);
                } else if (sorter) {
                  handleSorter(sorter as SorterResult<any>);
                }

                const mappedSorter = Array.isArray(sorter)
                  ? sorter.map(mapSorterField)
                  : sorter
                    ? mapSorterField(sorter as SorterResult<any>)
                    : sorter;

                tableProps.onChange?.(pagination, filters, mappedSorter, extra);
              }}
            />
          </SortableContext>
        </DndContext>
      ) : (
        <>
          <HeaderRow total={kanbanDataSource?.length} />
          <QuotesKanbanView
            t={t}
            dataSource={kanbanDataSource}
            isLoading={allQuotesLoading}
            isFetching={isFetching}
            filters={filters}
            setFilters={setFilters}
            setAllQuotes={setAllQuotes}
          />
        </>
      )}

      {children}
    </>
  );
};
